import heroImage from '../assets/aventure-kayak-lagon-tahaa.jpg'; 
import secondaryImage from '../assets/kayak-tahaa-lagon.jpg'; 
import aerialView from '../assets/jardindecorail-tahaa-lagon.jpg';
import googleIcon from '../assets/icone-google-avis-kayak.png';

import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, MapPin, Star, Fish, 
  Armchair, ShieldCheck, Waves, Navigation, Calendar as CalendarIcon,
  Droplets, Sparkles
} from 'lucide-react';
import { Button } from "../components/ui/button";
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import PhotoCarousel from '../components/PhotoCarousel';
import ReservationDetailsModal from '../components/ReservationDetailsModal';
import heroVideo from '../assets/videos/hero-tahaa-optimized.mp4';
import heroPoster from '../assets/videos/hero-poster.jpg';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const HomePage = () => {
  const reviews = [
    { 
      name: "Victoria Hadida", 
      platform: "Google", 
      text: "Une des plus belles journées de notre séjour en Polynésie ! Kayaks très confortables, matériel aquatique au top et conseils précieux d’Anaïs et Grégory qui connaissent parfaitement les alentours pour découvrir le jardin de corail." 
    },
    { 
      name: "Mathilde Parent", 
      platform: "Google", 
      text: "Trop chouette !! les kayaks sont confortables, ils sont un bon moyen de découvrir le jardin de corail sans passer par une excursion ! Merci à Greg et sa compagne pour leur accueil !" 
    },
    { 
      name: "Sébastien R.", 
      platform: "Google", 
      text: "Superbe expérience ! Kayaks très confortables et stables pour rejoindre le jardin de corail. L'accueil est au top, je recommande vivement." 
    },
    { 
      name: "Cécile B.", 
      platform: "Google", 
      text: "Matériel de qualité avec des dossiers qui font toute la différence pour le dos. Une liberté totale pour explorer le lagon à son rythme." 
    }
  ];

  const faqs = [
    {
      q: "Qu’est-ce que Tahaa Lagoon Kayak ?",
      a: "Tahaa Lagoon Kayak (anciennement Taha’a E-Paddle Adventure) propose des kayaks mono-place et bi-place en location pour rejoindre le Jardin de Corail sur l’île de Taha’a en Polynésie Française."
    },
    {
      q: "Quelles sont les expériences proposées ?",
      a: "Nous proposons la location de nos kayaks pour vous balader librement en totale autonomie. Idéal pour rejoindre le jardin de corail et vous profiter du snorkeling ou pour une sortie pêche sur le lagon ( hors jardin de corail ) !"
    },
    {
      q: "Dois-je avoir de l’expérience en kayak ou en snorkeling ?",
      a: "L’utilisation des kayaks ne nécessite pas réellement d'expérience préalable mais une condition physique minimum et être agé de 16 ans sous la responsabilité d'un parent. Pour le snorkeling, une capacité à nager est requise."
    },
    {
      q: "Quel équipement est fourni ?",
      a: "Nous fournissons l’équipement de sécurité obligatoire (gilet de sauvetage certifié) et des sacs étanches. En option (1000f / 8,50 €), nous proposons un kit snorkeling complet (masque, tuba, palmes, chaussures d'eau et shorty). À votre retour, une douche extérieure d'eau douce et des serviettes propres sont gracieusement mises à votre disposition pour vous rincer !"
    },
    {
      q: "Quelle est la durée et le tarif de location ?",
      a: "Les locations se font à la demi-journée ou journée. Le paiement s'effectue en espèces sur place (distributeurs à Patio ou Haamene). 3000f (25 €) > kayak monoplace / 5000f (42 €) > kayak biplace **Tarif résident : Réduction de -500f (4 €) par personne et par demi-journée sur justificatif.**"
    },
    {
      q: "Quelle est votre politique d’annulation ?",
      a: "La flexibilité est totale : vous pouvez annuler quand vous voulez car il n'y a pas de paiement d'avance."
    },
    {
      q: "Puis-je louer en cas de mauvais temps ?",
      a: "La sécurité est notre priorité. En cas de météo défavorable, nous vous contacterons pour reporter ou annuler. Vérifiez la [Météo Windy](https://www.windy.com/?-16.616,-151.531,11) ici."
    },
    {
      q: "Où se trouve Tahaa Lagoon Kayak ?",
      a: "Nous sommes situés à proximité du magasin Nohotua. Retrouvez-nous sur [Google Maps](https://maps.app.goo.gl/rUt1RX2mCdK2X9Ds9)."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Location Kayak Taha'a - Autonomie au Jardin de Corail</title>
        <meta name="description" content="Découvrez le lagon de Taha'a à votre rythme. Location de kayaks pour rejoindre le Jardin de Corail en autonomie. Kit snorkeling en option. Pour des tours guidés, réservez nos excursions bateau." />
        <meta property="og:title" content="Location Kayak Taha'a - Autonomie au Jardin de Corail" />
        <meta property="og:description" content="Découvrez le lagon de Taha'a à votre rythme avec nos kayaks biplaces et monoplaces. Pour des tours guidés, réservez nos excursions bateau." />
        <meta name="twitter:title" content="Location Kayak Taha'a - Autonomie au Jardin de Corail" />
        <meta name="twitter:description" content="Découvrez le lagon de Taha'a à votre rythme avec nos kayaks biplaces et monoplaces. Pour des tours guidés, réservez nos excursions bateau." />
        <link rel="canonical" href="https://tahaaepaddleadventure.com/" />
        <link rel="preload" as="image" href={heroPoster} fetchPriority="high" />
      </Helmet>

      <ScrollToTop />
      
      {/* SECTION 1 : HERO */}
      <div className="relative h-[90vh] flex items-start justify-center overflow-hidden pt-24 md:pt-32">
        <div className="absolute inset-0 z-0 bg-slate-900">
          <img 
            src={heroPoster}
            alt="Magnifique lagon de Taha'a"
            className="w-full h-full object-cover"
            fetchpriority="high"
          />
          <video 
            src={heroVideo}
            autoPlay 
            loop 
            muted 
            playsInline
            aria-hidden="true" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30" /> 
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <span className="uppercase tracking-[0.2em] text-cyan-400 font-bold text-sm md:text-base mb-4 block drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">
            Anciennement Taha'a E-Paddle Adventure
          </span>
          <h1 className="text-4xl md:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-[0_4px_4px_rgba(0,0,0,0.8)]">
            Tahaa Lagoon Kayak <br/> 
            <span className="text-cyan-200 text-3xl md:text-5xl font-light italic">
                Location Kayak à Taha'a en Autonomie
            </span>
          </h1>
          <p className="text-white text-lg md:text-xl font-medium max-w-2xl mx-auto mb-8 drop-shadow-md">
            Partez explorer le Jardin de Corail à votre rythme. Kit snorkeling disponible en option.
            <br/><br/>
            <span className="text-sm md:text-base font-normal opacity-90">
              *Vous préférez être accompagnés ? Découvrez nos excursions guidées en bateau sur <a href="https://tahaa-excursions.com" target="_blank" rel="noreferrer" className="underline font-bold text-amber-300 hover:text-amber-400">Tahaa Lagoon Explorer</a>.
            </span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/location-de-kayak/">
              <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full px-10 h-16 shadow-2xl text-lg border-2 border-white/20">
                Réserver mon Kayak
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* SECTION GALERIE CARROUSEL */}
      <PhotoCarousel />

      {/* SECTION 2 : L'AVENTURE AUTONOME */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative group order-2 md:order-1">
              <div className="absolute -inset-4 bg-cyan-100/40 rounded-[3rem] scale-95 group-hover:scale-100 transition-transform duration-700 -rotate-2"></div>
              <div className="relative aspect-[4/3] rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white">
                <img 
                  src={secondaryImage} 
                  width="800"
                  height="600"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" 
                  alt="Exploration libre du lagon de Taha'a en kayak biplace avec Tahaa Lagoon Kayak" 
                />
              </div>
            </div>
            <div className="space-y-8 order-1 md:order-2 text-center md:text-left">
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-[1.1]">
                Explorez le lagon de Taha'a <br/>
                <span className="text-cyan-600">en toute autonomie</span>
              </h2>
              <div className="space-y-5 text-gray-600 text-lg leading-relaxed">
                <p>
                  Taha'a E-Paddle Adventure devient <strong>Tahaa Lagoon Kayak</strong> ! Pour vous offrir une immersion totale, nous avons sélectionné des <strong>kayaks haut de gamme</strong> alliant stabilité et confort exceptionnel.
                </p>
                <p>
                  Oubliez les contraintes des groupes. Pagayez vers le célèbre <strong>Jardin de Corail</strong>, ancrez-vous et profitez du spectacle sous-marin en toute liberté.
                </p>
              </div>
              <div className="relative p-6 rounded-2xl bg-slate-50 border-l-8 border-cyan-500 shadow-sm">
                <p className="text-slate-700 font-medium italic text-lg leading-snug">
                  "Une navigation silencieuse et écologique pour préserver la magie du lagon polynésien."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 : ÉQUIPEMENT */}
      <section className="py-20 bg-gradient-to-b from-sky-50 to-white text-slate-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-12">Equipements de kayak haut de gamme</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-sky-100 transition-all hover:shadow-md">
              <div className="text-cyan-600 mb-6 flex justify-center"><Armchair size={48} strokeWidth={1.5}/></div>
              <h3 className="text-xl font-bold mb-4">Confort Optimal</h3>
              <p className="text-slate-600">Sièges ergonomiques avec maintien du dos et cales pieds ajustable pour pagayer sereinement.</p>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-sky-100 transition-all hover:shadow-md">
              <div className="text-cyan-600 mb-6 flex justify-center"><Fish size={48} strokeWidth={1.5}/></div>
              <h3 className="text-xl font-bold mb-4">Passion Pêche</h3>
              <p className="text-slate-600">Supports de canne intégrés pour vos sessions de pêche dans les zones autorisées du lagon.</p>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-sky-100 transition-all hover:shadow-md">
              <div className="text-cyan-600 mb-6 flex justify-center"><ShieldCheck size={48} strokeWidth={1.5}/></div>
              <h3 className="text-xl font-bold mb-4">Rangement Sécurisé</h3>
              <p className="text-slate-600">Sacs étanches fournis pour garder vos affaires et téléphone au sec.</p>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-cyan-200 transition-all hover:shadow-md ring-2 ring-cyan-500/20">
              <div className="text-cyan-600 mb-6 flex justify-center"><Droplets size={48} strokeWidth={1.5}/></div>
              <h3 className="text-xl font-bold mb-4">Douche & Serviette</h3>
              <p className="text-slate-600">Douche extérieure d'eau douce et serviette propre fournies pour vous rincer au retour.</p>
            </div>
          </div>
          <Link to="/materiels-equipements/">
            <Button variant="outline" className="border-cyan-600 text-cyan-600 hover:bg-cyan-600 hover:text-white rounded-full px-8 h-12 font-bold transition-all duration-300">
              Voir tous les détails <ChevronRight size={18} className="ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      {/* SECTION CALENDRIER DES DISPONIBILITÉS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Calendrier des Disponibilités</h2>
            <p className="text-slate-600 text-lg">Consultez en direct nos places disponibles pour réserver votre kayak.</p>
          </div>
          <div className="flex justify-center">
            <AvailabilityCalendar />
          </div>
        </div>
      </section>

      {/* SECTION 4 : ITINERAIRE */}
      <section className="py-20 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-12 text-slate-900">Traversée en kayak direction : Le Jardin de Corail de Taha'a</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            <div className="bg-slate-50 p-8 rounded-3xl shadow-sm border border-slate-100 transition-all hover:shadow-md">
              <div className="text-cyan-600 mb-6 flex justify-center">
                <MapPin size={56} strokeWidth={1.5}/>
              </div>
              <h3 className="font-bold text-xl mb-3 text-slate-900">Départ Base</h3>
              <p className="text-slate-600">
                Nous sommes situés à proximité du magasin Nohotua. 
                <br />
                <a 
                  href="https://maps.app.goo.gl/RMYmt5YjjtZzMf8p9" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-cyan-600 hover:underline font-semibold block mt-3"
                >
                  Voir l'adresse sur Google Maps
                </a>
              </p>
            </div>

            <div className="bg-sky-50 p-8 rounded-3xl shadow-sm border border-sky-100 transition-all hover:shadow-md ring-1 ring-sky-200/50">
              <div className="text-cyan-600 mb-6 flex justify-center">
                <Navigation size={56} strokeWidth={1.5}/>
              </div>
              <h4 className="font-bold text-xl mb-3 uppercase tracking-wide text-cyan-900">La Traversée</h4>
              <p className="text-cyan-800">
                <strong>2 km (env. 45 min)</strong> de navigation sur des eaux cristallines.
              </p>
            </div>

            <div className="bg-slate-50 p-8 rounded-3xl shadow-sm border border-slate-100 transition-all hover:shadow-md">
              <div className="text-cyan-600 mb-6 flex justify-center">
                <Waves size={56} strokeWidth={1.5}/>
              </div>
              <h3 className="font-bold text-xl mb-3 text-slate-900">Livraison</h3>
              <p className="text-slate-600">
                Explorez le magnifique Jardin de Corail entre les motus. Ancrez votre kayak et profitez du spectacle.
              </p>
            </div>

          </div>
        </div>
      </section>
      
      {/* SECTION BANDEAU PHOTO AÉRIENNE */}
      <section className="w-full h-[400px] md:h-[600px] overflow-hidden">
        <img src={aerialView} width="1920" height="1080" loading="lazy" className="w-full h-full object-cover" alt="Vue aérienne panoramique du Jardin de Corail et des Motus de Taha'a" />
      </section>

      {/* SECTION 5 : FAQ (FIXE POUR SEO) */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">Questions Fréquentes sur la location</h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-4 border-b border-cyan-50 pb-3">{faq.q}</h3>
                <div className="text-slate-600 leading-relaxed text-lg">
                  {faq.a.split('[').map((part, i) => {
                    if (i === 0) return part;
                    const [linkText, rest] = part.split('](');
                    const [url, end] = rest.split(')');
                    return (
                      <React.Fragment key={i}>
                        <a href={url} target="_blank" rel="noreferrer" className="text-cyan-600 underline font-bold hover:text-cyan-700">{linkText}</a>
                        {end}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION AVIS */}
      <section className="py-20 bg-white border-t border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 italic">Ce que nos clients disent de Tahaa Lagoon Kayak</h2>
            <div className="flex justify-center mt-6">
                <img src={googleIcon} alt="Avis clients Google Taha'a Lagoon Kayak" width="200" height="32" loading="lazy" className="h-8 w-auto opacity-90" />
            </div>
          </div>
          <div className="relative">
            <div className="flex animate-scroll gap-6 md:gap-8 py-4">
              {[...reviews, ...reviews].map((rev, i) => (
                <div key={i} className="min-w-[320px] md:min-w-[450px] bg-slate-50 p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, starI) => (
                      <Star key={starI} size={16} className="fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 italic mb-6 text-base leading-relaxed">"{rev.text}"</p>
                  <div className="flex justify-between items-center border-t border-slate-200 pt-4">
                    <span className="font-bold text-slate-900">{rev.name}</span>
                    <span className="text-xs font-bold text-cyan-600 bg-cyan-50 px-3 py-1 rounded-full uppercase tracking-wider">{rev.platform}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <style dangerouslySetInnerHTML={{ __html: `
          @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          .animate-scroll { display: flex; width: max-content; animation: scroll 40s linear infinite; }
          .animate-scroll:hover { animation-play-state: paused; }
          [class*="emergent"], #emergent-badge, .made-with-emergent { display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; }
        `}} />
      </section>

      {/* SECTION CTA FINAL */}
      <section className="py-24 bg-[#E0F7FA] text-slate-900">
        <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold mb-12 italic text-[#00A3B1]">Prêt pour l'évasion ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
               <div className="bg-white p-10 rounded-[2rem] border border-cyan-100 shadow-xl relative flex flex-col justify-between">
                  <div>
                    <div className="mb-6">
                        <span className="bg-yellow-400 text-slate-900 text-sm font-bold px-4 py-2 rounded-full inline-flex items-center gap-2 shadow-md">
                           🎁 Tarif Résident : -500 F (4 €) / pers. / demi-journée (sur justificatif)
                        </span>
                    </div>
                    <h3 className="font-bold text-2xl mb-4 text-[#1A1A1A]">Louer un Kayak à Taha'a</h3>
                    <p className="mb-8 text-slate-600 font-medium leading-relaxed text-lg">Liberté, sport et nature. Découvrez le lagon à votre rythme à partir de 3000 f (25 €).</p>
                  </div>
                  <Link to="/location-de-kayak/">
                   <Button className="w-full bg-[#00A3B1] text-white hover:bg-[#008c99] font-extrabold h-14 rounded-xl text-lg shadow-lg transition-transform hover:scale-[1.02] border-none">
                     Réserver mon Kayak
                   </Button>
                  </Link>
               </div>
               <div className="bg-white p-10 rounded-[2rem] border border-cyan-100 shadow-xl flex flex-col justify-between">
                  <div>
                    <div className="mb-6 h-9"></div>
                    <h3 className="font-bold text-2xl mb-4 text-[#1A1A1A] text-center">Excursion en Bateau Privé</h3>
                    <p className="mb-8 text-slate-600 font-medium leading-relaxed text-lg text-center">Privilégiez le confort et l'expertise d'un guide avec Greg & Anaïs.</p>
                  </div>
                  <a href="https://tahaa-excursions.com" target="_blank" rel="noreferrer">
                   <Button className="w-full bg-[#ff7b25] hover:bg-[#e66a1a] text-white font-extrabold h-14 rounded-xl text-lg shadow-lg transition-transform hover:scale-[1.02] border-none">
                     Visiter Tahaa Lagoon Explorer
                   </Button>
                  </a>
               </div>
            </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;